/**
 * Record codes — the short URL every record goes by (KPE-002, KPR-206).
 *
 * The API gives each collection, item, card definition, tag, element set and
 * multi-add form a `code` (keepr-api utils/recordCodes.js, docs/SCHEMA.md
 * § Record codes): 7+ characters of lowercase Crockford base32. Its address is
 * `/<letter>/<code>`:
 *
 *   /c/<code>[/…]   a collection and every page beneath it   /collections/<id>[/…]
 *   /i/<code>       an item                                   /collections/<c>/items/<id>
 *   /d/<code>       a card definition                         /cards/<id>
 *   /t/<code>       a tag                                     /tags/<id>
 *   /e/<code>       an element set                            /element-sets/<id>
 *   /f/<code>       a multi-add form                          /collections/<c>/multi-add/<id>
 *
 * THE ROUTER NEVER SEES A CODE. The app's routes, params, id comparisons and
 * storage keys all stay on ObjectIds; the translation happens at the history
 * boundary (utils/shortUrlWindow.js): the address bar shows the short form,
 * the router reads the long one. This module is the dictionary both
 * directions read:
 *
 *   - `learn` — every API answer that carries `{ _id, code }` teaches it the
 *     record's code (and its collection), harvested once in api/client.js;
 *   - `toShortPath` — a long path → its short form, when every code it needs
 *     is known (else the path as it was);
 *   - `toLongPath` — a short path → the long one, when the code is known
 *     (else the path as it was: the short routes, pages/ShortRecordRoute.js,
 *     then ask the API).
 *
 * Kept in localStorage so a reload or a new tab reads short URLs without a
 * round trip. Ids and codes are not secrets — both only name what the API
 * would show this person anyway — but the store is capped and every access
 * is guarded: a blocked storage just means one more resolve.
 */

export const ALPHABET = '0123456789abcdefghjkmnpqrstvwxyz';
const CODE_RE = /^[0-9a-hjkmnp-tv-z]{7,16}$/;
const HEX_RE = /^[0-9a-f]{24}$/i;

/** kind → its URL letter (the owner's decision, 2026-10-04) and the resolve endpoint's name for it. */
export const KINDS = Object.freeze({
	collection: 'c',
	item: 'i',
	card: 'd',
	tag: 't',
	'element-set': 'e',
	'multi-add-form': 'f',
});
const KIND_OF_LETTER = Object.freeze(Object.fromEntries(Object.entries(KINDS).map(([kind, letter]) => [letter, kind])));

export const kindOfLetter = letter => KIND_OF_LETTER[String(letter || '').toLowerCase()] || null;

/**
 * A typed or pasted code → its stored form, or null. Case folds; i and l
 * read as 1, o as 0 (Crockford); a hyphen is spacing. Mirrors the API's
 * normalizeCode so a code that resolves there translates here.
 */
export function normalizeCode(raw) {
	if (typeof raw !== 'string') return null;
	const code = raw.trim().toLowerCase().replace(/-/g, '').replace(/[il]/g, '1').replace(/o/g, '0');
	return CODE_RE.test(code) ? code : null;
}

export const isCode = value => typeof value === 'string' && CODE_RE.test(value);
export const isObjectId = value => typeof value === 'string' && HEX_RE.test(value);

// ---------------------------------------------------------------- the store

const STORAGE_KEY = 'keepr.recordCodes.v1';
const CAP = 4000;

// id → { code, c (collection id or null) }, in insertion (recency) order.
let byId = new Map();
// `${letter}:${code}` → { id, c }
let byCode = new Map();
let loaded = false;
// Bumped whenever something new is learned (hooks/useRecordCodes reads it).
let version = 0;
let saveTimer = null;
const listeners = new Set();

function load() {
	if (loaded) return;
	loaded = true;
	try {
		const raw = window.localStorage.getItem(STORAGE_KEY);
		if (!raw) return;
		const rows = JSON.parse(raw);
		if (!Array.isArray(rows)) return;
		for (const row of rows) {
			if (!Array.isArray(row)) continue;
			const [id, code, c, letter] = row;
			if (!isObjectId(id) || !isCode(code)) continue;
			byId.set(id, { code, c: isObjectId(c) ? c : null });
			if (letter && KIND_OF_LETTER[letter]) byCode.set(`${letter}:${code}`, { id, c: isObjectId(c) ? c : null });
		}
	} catch {
		// Unreadable or blocked storage: start empty.
	}
}

function save() {
	if (saveTimer) return;
	saveTimer = setTimeout(() => {
		saveTimer = null;
		try {
			const letters = new Map();
			for (const [key, { id }] of byCode) letters.set(id, key.split(':')[0]);
			const rows = [...byId].slice(-CAP).map(([id, { code, c }]) => [id, code, c, letters.get(id) || null]);
			window.localStorage.setItem(STORAGE_KEY, JSON.stringify(rows));
		} catch {
			// Full or blocked storage: the in-memory map still serves this page.
		}
	}, 250);
}

function trim() {
	while (byId.size > CAP) byId.delete(byId.keys().next().value);
	while (byCode.size > CAP) byCode.delete(byCode.keys().next().value);
}

function remember(id, code, collectionId, letter = null) {
	load();
	const prior = byId.get(id);
	const c = isObjectId(collectionId) ? collectionId : (prior ? prior.c : null);
	const changed = !prior || prior.code !== code || prior.c !== c;
	if (prior) byId.delete(id); // re-insert: most recent last
	byId.set(id, { code, c });
	if (letter) byCode.set(`${letter}:${code}`, { id, c });
	trim();
	if (changed) {
		version++;
		save();
		listeners.forEach(fn => { try { fn(); } catch { /* a listener's failure is its own */ } });
	}
}

const hexOf = value => {
	if (typeof value === 'string') return value;
	if (value && typeof value === 'object' && typeof value.$oid === 'string') return value.$oid;
	return null;
};

/** Teach the store one record: anything with `_id` and `code` (and, when it has one, `collection_id`). */
export function learnRecord(record, letter = null) {
	if (!record || typeof record !== 'object') return;
	const id = hexOf(record._id);
	const code = typeof record.code === 'string' ? record.code : null;
	if (!isObjectId(id) || !isCode(code)) return;
	remember(id.toLowerCase(), code, hexOf(record.collection_id), letter);
}

/**
 * Walk an API answer for records that carry a code — the harvest
 * api/client.js runs on every response. Bounded: a deep or huge answer is
 * read only so far, which costs a missed shortening, never a wrong one.
 */
export function learnFrom(data, { maxDepth = 5, maxNodes = 5000 } = {}) {
	let nodes = 0;
	const walk = (value, depth) => {
		if (nodes > maxNodes || depth > maxDepth || !value || typeof value !== 'object') return;
		nodes++;
		if (Array.isArray(value)) {
			for (const entry of value) walk(entry, depth + 1);
			return;
		}
		if (value.code !== undefined && value._id !== undefined) learnRecord(value);
		for (const key of Object.keys(value)) {
			const child = value[key];
			if (child && typeof child === 'object') walk(child, depth + 1);
		}
	};
	try { walk(data, 0); } catch { /* never break an answer over a harvest */ }
}

/** What the resolve endpoint answered: `{ kind, code, id, collection_id }`. */
export function learnResolved({ kind, code, id, collection_id: c } = {}) {
	const letter = KINDS[kind];
	if (!letter || !isCode(code) || !isObjectId(id)) return;
	remember(id.toLowerCase(), code, c, letter);
}

export const codeOf = id => { load(); return (isObjectId(id) && byId.get(id.toLowerCase())?.code) || null; };

export function lookup(letter, code) {
	load();
	return byCode.get(`${letter}:${code}`) || null;
}

/** How many times the store has learned something new: a cache key for a value built from it. */
export const codesVersion = () => version;

/** Called whenever the store learns something new (the address-bar canonicaliser listens). */
export function subscribe(fn) {
	listeners.add(fn);
	return () => listeners.delete(fn);
}

// ---------------------------------------------------------------- paths

function splitPath(path) {
	const s = String(path ?? '');
	const hashAt = s.indexOf('#');
	const beforeHash = hashAt === -1 ? s : s.slice(0, hashAt);
	const hash = hashAt === -1 ? '' : s.slice(hashAt);
	const queryAt = beforeHash.indexOf('?');
	return {
		pathname: queryAt === -1 ? beforeHash : beforeHash.slice(0, queryAt),
		search: queryAt === -1 ? '' : beforeHash.slice(queryAt),
		hash,
	};
}

/** The short form of `/<letter>/<code>`, recorded so the way back is known. */
function emit(letter, id, c) {
	const entry = byId.get(id);
	if (!entry) return null;
	byCode.set(`${letter}:${entry.code}`, { id, c: c || entry.c || null });
	return `/${letter}/${entry.code}`;
}

/**
 * A long in-app path (with its query and hash) → its short form, or the path
 * unchanged when a code it needs is unknown. Never touches anything but the
 * six record shapes.
 */
export function toShortPath(path) {
	load();
	const { pathname, search, hash } = splitPath(path);
	const parts = pathname.split('/');
	// ['', 'collections', '<id>', ...rest]
	let short = null;
	if (parts[1] === 'collections' && isObjectId(parts[2])) {
		const cid = parts[2].toLowerCase();
		const rest = parts.slice(3);
		if (rest[0] === 'items' && isObjectId(rest[1]) && rest.length === 2 && codeOf(rest[1])) {
			short = emit('i', rest[1].toLowerCase(), cid);
		} else if (rest[0] === 'multi-add' && isObjectId(rest[1]) && rest.length === 2 && codeOf(rest[1])) {
			short = emit('f', rest[1].toLowerCase(), cid);
		} else if (codeOf(cid)) {
			const head = emit('c', cid, null);
			short = rest.length ? `${head}/${rest.join('/')}` : head;
		}
	} else if (parts.length === 3 && isObjectId(parts[2]) && codeOf(parts[2])) {
		const letter = { cards: 'd', tags: 't', 'element-sets': 'e' }[parts[1]];
		if (letter) short = emit(letter, parts[2].toLowerCase(), null);
	}
	if (!short) return path;
	return `${short}${search}${hash}`;
}

/**
 * The long path a short one stands for: `{ path, kind, code }` when `path`
 * is a short record path — `path` null while the code is unknown (the short
 * route asks the API) — or null when it is no short record path at all.
 * An ObjectId in place of a code needs no lookup, except where the long form
 * needs a collection the id alone does not give (an item, a multi add).
 */
export function parseShortPath(path) {
	const { pathname, search, hash } = splitPath(path);
	const parts = pathname.split('/');
	const kind = kindOfLetter(parts[1]);
	// A letter in any case (a QR code carries the URL in capitals).
	if (!kind || parts.length < 3 || parts[1].length !== 1) return null;
	const letter = KINDS[kind];
	const raw = parts[2];
	const rest = parts.slice(3);
	// Only a collection has pages beneath it.
	if (rest.length && letter !== 'c') return null;
	const tail = `${rest.length ? `/${rest.join('/')}` : ''}${search}${hash}`;
	const longFor = (id, c) => {
		switch (letter) {
			case 'c': return `/collections/${id}${tail}`;
			case 'i': return c ? `/collections/${c}/items/${id}${tail}` : null;
			case 'f': return c ? `/collections/${c}/multi-add/${id}${tail}` : null;
			case 'd': return `/cards/${id}${tail}`;
			case 't': return `/tags/${id}${tail}`;
			case 'e': return `/element-sets/${id}${tail}`;
			default: return null;
		}
	};
	if (isObjectId(raw)) {
		const id = raw.toLowerCase();
		const c = byId.get(id)?.c || null;
		return { kind, letter, code: null, id, tail, path: longFor(id, c) };
	}
	const code = normalizeCode(raw);
	if (!code) return { kind, letter, code: null, id: null, tail, path: null, malformed: true };
	load();
	const hit = byCode.get(`${letter}:${code}`);
	return { kind, letter, code, id: hit ? hit.id : null, tail, path: hit ? longFor(hit.id, hit.c) : null, longFor };
}

/** A short path → the long one when it can be read now, else the path unchanged. */
export function toLongPath(path) {
	const parsed = parseShortPath(path);
	return parsed && parsed.path ? parsed.path : path;
}

/** The long path for a resolved answer plus the short URL's tail. */
export function longPathFor({ kind, id, collection_id: c }, tail = '') {
	const id_ = String(id).toLowerCase();
	switch (KINDS[kind]) {
		case 'c': return `/collections/${id_}${tail}`;
		case 'i': return `/collections/${c}/items/${id_}${tail}`;
		case 'f': return `/collections/${c}/multi-add/${id_}${tail}`;
		case 'd': return `/cards/${id_}${tail}`;
		case 't': return `/tags/${id_}${tail}`;
		case 'e': return `/element-sets/${id_}${tail}`;
		default: return null;
	}
}

/** Tests only. */
export function _reset() {
	byId = new Map();
	byCode = new Map();
	loaded = false;
	if (saveTimer) clearTimeout(saveTimer);
	saveTimer = null;
	listeners.clear();
}
